/**
 * START-WEG — welche Seite die App beim Öffnen zeigt.
 *
 * Herausgelöst aus `main.ts` (02.11.2026, Bahn stoerung-login), damit die
 * Entscheidung prüfbar ist: `main.ts` lädt sich beim Import selbst und hat
 * darum keinen Test.
 *
 * ═══ 🔴 DIE LOGIN-SCHLEIFE VOM 29.10.2026 ═══════════════════════════════════
 * 📏 07:56–08:23 UTC war die Prod-D1 weg. Der Start las die Fehlerantwort von
 * `/me` als „nicht angemeldet“, fragte die Profil-Zuordnung ab — die fiel
 * ebenfalls aus — und löschte im `catch` die Anmeldung. ⇒ Anmeldemaske für alle.
 *
 * 🔑 Gelöscht wird die Anmeldung nur noch, wenn der Server ein URTEIL gefällt
 * hat (z. B. 403 „keine Workspace-Mail“), nie bei einer Störung.
 * Entscheid Justin 02.11.: Störung = 5xx, keine Verbindung, unlesbare Antwort.
 */

import { claimProfiles, ApiError, type ClaimProfilesResult } from "../api/client";
import type { UserContext } from "../api/types";
import { probeAuthStatus, type AuthProbe } from "./auth-guard";
import { clearToken, getToken } from "./token";

export type StartWeg =
  | { weg: "app"; user: UserContext }
  | { weg: "stoerung"; grund: string }
  | { weg: "claim"; claim: ClaimProfilesResult }
  | { weg: "login" };

export type StartWegDeps = {
  probe: () => Promise<AuthProbe>;
  hatToken: () => boolean;
  claim: () => Promise<ClaimProfilesResult>;
  clearToken: () => void;
};

const ECHTE_DEPS: StartWegDeps = {
  probe: probeAuthStatus,
  hatToken: () => Boolean(getToken()),
  claim: claimProfiles,
  clearToken
};

/** Eine Antwort, die kein Urteil ist: keine Verbindung (0) oder Serverfehler (5xx). */
export function istStoerung(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.statusCode === 0 || err.statusCode >= 500;
}

export async function bestimmeStartWeg(deps: StartWegDeps = ECHTE_DEPS): Promise<StartWeg> {
  const p = await deps.probe();
  if (p.art === "gestoert") return { weg: "stoerung", grund: p.grund };
  if (p.art === "angemeldet") return { weg: "app", user: p.user };

  // Phase 7.7: Token vorhanden, aber kein Profil (/me 403)? → Profil-Selbst-
  // zuordnung anbieten, sofern Workspace-Mail + wählbare Profile. Sonst Login.
  if (deps.hatToken()) {
    try {
      return { weg: "claim", claim: await deps.claim() };
    } catch (err) {
      if (istStoerung(err)) {
        const code = err instanceof ApiError ? err.statusCode : 0;
        return { weg: "stoerung", grund: code ? `Server antwortet mit ${code}` : "keine Verbindung zum Server" };
      }
      // keine Workspace-Mail / Claim deaktiviert → normaler Login
      deps.clearToken();
    }
  }
  return { weg: "login" };
}
